import React from "react";
import { timeToX } from "../utils/timelineCalculations";

interface VideoSegment {
  start: number;
  end: number;
  hasVideo: boolean;
}

interface VideoTrackProps {
  segments: VideoSegment[];
  /**
   * Stretches of the window this client has never PROBED for footage.
   *
   * A blank on this track already means "recorded, not uploaded yet". An
   * unprobed stretch means "nobody has asked" — different cause, different
   * resolution, and only one of them is going to change on its own. They look
   * identical without this, which is what makes a strip that has fallen behind
   * (returning to the tab after minutes away) read as "this machine stopped
   * recording".
   */
  pendingRanges?: { from: number; to: number }[];
  min: number;
  max: number;
  scaledWidth: number;
  viewBoxHeight: number;
}

export const VideoTrack: React.FC<VideoTrackProps> = ({
  segments,
  pendingRanges = [],
  min,
  max,
  scaledWidth,
  viewBoxHeight,
}) => {
  // Still render for the pending band alone: a strip with no segments YET is
  // exactly the case worth speaking for.
  if (segments.length === 0 && pendingRanges.length === 0) return null;

  return (
    <g transform={`translate(0, ${viewBoxHeight - 30})`}>
      <defs>
        {/* Diagonal hatch: reads as "unknown" at a glance and stays legible at
            one-pixel widths, where a fill-opacity difference would not. */}
        <pattern id="video-track-pending" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="rgba(148, 163, 184, 0.10)" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(148, 163, 184, 0.45)" strokeWidth="2" />
        </pattern>
      </defs>

      {/* Background for video track */}
      <rect
        x={0}
        y={0}
        width={scaledWidth}
        height={25}
        fill="rgba(0, 0, 0, 0.05)"
        stroke="#d1d5db"
        strokeWidth={0.5}
      />

      {/* Not probed yet — drawn UNDER the segments so arriving footage simply
          covers it. Neutral and striped rather than amber: amber on this track
          already means "recorded, not uploaded", and reusing it would merge two
          states an operator has to tell apart. */}
      {pendingRanges.map((range, i) => {
        const startX = timeToX(range.from, min, max, scaledWidth);
        const endX = timeToX(range.to, min, max, scaledWidth);
        return (
          <rect
            key={`pending-${i}`}
            x={startX}
            y={2}
            width={Math.max(1, endX - startX)}
            height={21}
            fill="url(#video-track-pending)"
            rx={2}
            pointerEvents="none"
          />
        );
      })}

      {/* Video availability segments */}
      {segments.map((segment, i) => {
        const startX = timeToX(segment.start, min, max, scaledWidth);
        const endX = timeToX(segment.end, min, max, scaledWidth);
        const segWidth = Math.max(1, endX - startX);

        return (
          <rect
            key={i}
            x={startX}
            y={2}
            width={segWidth}
            height={21}
            fill="rgba(96, 165, 250, 0.3)"
            stroke="rgba(59, 130, 246, 0.5)"
            strokeWidth={1}
            rx={2}
          />
        );
      })}
    </g>
  );
};
